import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../config/supabase.js'
import { getUserCompanies } from '../services/api.js'
import { logger } from '../utils/logger.js'
import { clearAllAppData } from '../utils/sessionManager.js'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const { t } = useTranslation()
  const [user, setUser] = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [companiesLoading, setCompaniesLoading] = useState(false)
  const [companies, setCompanies] = useState([])
  const [activeCompany, setActiveCompany] = useState(null)
  const [hasCompanyAccess, setHasCompanyAccess] = useState(false)
  const [companyError, setCompanyError] = useState(null)

  // Fetch user companies after successful authentication
  const fetchUserCompanies = useCallback(async () => {
    try {
      setCompaniesLoading(true)
      setCompanyError(null)

      // Verify there is a valid session before calling the API.
      // If getSession() returns null the stored token is stale or in an
      // incompatible format (e.g. after a supabase-js major upgrade).
      // Sign out locally so the user sees the login screen with a clear
      // message rather than a misleading "no company access" error.
      const { data: { session: currentSession } } = await supabase.auth.getSession()
      if (!currentSession) {
        logger.warn('fetchUserCompanies: no active session – clearing stale auth state')
        await supabase.auth.signOut({ scope: 'local' })
        setUser(null)
        setSession(null)
        setCompanies([])
        setActiveCompany(null)
        setHasCompanyAccess(false)
        setCompanyError(null)
        setCompaniesLoading(false)
        return
      }

      const response = await getUserCompanies()
      const userCompanies = response.companies || []
      
      setCompanies(userCompanies)
      
      if (userCompanies.length > 0) {
        const urlCompanyId = typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search).get('company')
          : null
        const fromUrl = urlCompanyId
          ? userCompanies.find((c) => String(c.id) === urlCompanyId)
          : null
        const storedId = typeof window !== 'undefined' ? localStorage.getItem('revetall-activeCompanyId') : null
        const fromStorage = storedId ? userCompanies.find((c) => String(c.id) === storedId) : null
        const preferred = fromUrl || fromStorage
        setActiveCompany(preferred || userCompanies[0])
        setHasCompanyAccess(true)
      } else {
        // User has no company access
        setActiveCompany(null)
        setHasCompanyAccess(false)
        setCompanyError(t('errors.noCompanyAccess'))
      }
    } catch (error) {
      logger.error('Failed to fetch companies:', error)
      setCompanies([])
      setActiveCompany(null)
      setHasCompanyAccess(false)
      setCompanyError(error.message || t('errors.failedToLoadCompanyInfo'))
    } finally {
      setCompaniesLoading(false)
    }
  }, [t])

  // Switch to a different company (no full page reload; state update is enough)
  const switchCompany = useCallback((company) => {
    localStorage.setItem('revetall-activeCompanyId', String(company.id))
    setActiveCompany(company)
  }, [])

  useEffect(() => {
    const SESSION_TIMEOUT_MS = 10000

    const getSession = async () => {
      let timedOut = false
      const timeoutId = setTimeout(() => {
        timedOut = true
        logger.warn('getSession timed out - stopping loading state')
        setLoading(false)
      }, SESSION_TIMEOUT_MS)

      try {
        const { data: { session }, error } = await supabase.auth.getSession()
        clearTimeout(timeoutId)
        if (timedOut) return

        if (error) {
          logger.error('Error getting session:', error)
        } else {
          setSession(session)
          setUser(session?.user ?? null)

          if (session?.user) {
            fetchUserCompanies().catch(err => {
              logger.error('Failed to fetch companies on session restore:', err)
            })
          }

          setLoading(false)
        }
      } catch (error) {
        clearTimeout(timeoutId)
        logger.error('Error in getSession:', error)
        setLoading(false)
      }
    }

    getSession()

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session)
        setUser(session?.user ?? null)

        if (event === 'SIGNED_OUT') {
          setCompanies([])
          setActiveCompany(null)
          setHasCompanyAccess(false)
          setCompanyError(null)
          clearAllAppData()
        }

        // Load companies when session is restored (e.g. INITIAL_SESSION) so we don't rely only on getSession()
        if (session?.user && (event === 'INITIAL_SESSION' || event === 'SIGNED_IN')) {
          fetchUserCompanies().catch(err => {
            logger.error('Failed to fetch companies on auth state change:', err)
          })
        }

        setLoading(false)
      }
    )

    return () => subscription.unsubscribe()
  }, [fetchUserCompanies])

  // Sign up with email and password
  const signUp = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    })
    return { data, error }
  }, [])

  // Sign in with email and password
  const signIn = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    
    // Fetch companies after successful login
    if (data?.user && !error) {
      fetchUserCompanies().catch(error => {
        logger.error('Failed to fetch companies after login:', error)
      })
    }
    
    return { data, error }
  }, [fetchUserCompanies])

  // Sign out
  const signOut = useCallback(async () => {
    try {
      // Sign out from Supabase
      const { error } = await supabase.auth.signOut()
      
      // Log but ignore 403/session errors - they mean the session is already invalid/expired
      // In this case, we should still clean up local state
      if (error) {
        const is403Error = error.status === 403 || 
                          error.message?.includes('403') ||
                          error.message?.includes('session_id') ||
                          error.message?.includes('Session not found')
        
        if (is403Error) {
          logger.info('Session already invalid - proceeding with local cleanup')
        } else {
          logger.error('Error signing out:', error)
        }
      }
      
      // Force clear any remaining session data (local cleanup)
      // Ignore errors here too - if it fails, the finally block will still clean up
      try {
        await supabase.auth.signOut({ scope: 'local' })
      } catch (localError) {
        logger.warn('Local signout also failed, but continuing cleanup:', localError)
      }
      
    } catch (err) {
      // Catch any exceptions and log them, but don't let them prevent cleanup
      logger.error('Error during sign out process:', err)
    } finally {
      // ALWAYS force local cleanup regardless of Supabase response
      // This ensures user can log out even with invalid/expired sessions
      
      // Clear auth state
      setUser(null)
      setSession(null)
      
      // Clear company data
      setCompanies([])
      setActiveCompany(null)
      setHasCompanyAccess(false)
      setCompanyError(null)
      
      // Clear app data (chat history is preserved)
      clearAllAppData()
      
      logger.info('✅ User signed out - local state cleared')
    }
  }, [])

  // Reset password
  const resetPassword = useCallback(async (email) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email)
    return { data, error }
  }, [])

  // Compute combined loading state
  const isFullyLoaded = !loading && !companiesLoading
  
  const value = useMemo(() => ({
    user,
    session,
    loading: !isFullyLoaded,
    signUp,
    signIn,
    signOut,
    resetPassword,
    companies,
    activeCompany,
    hasCompanyAccess,
    companyError,
    switchCompany,
  }), [
    user,
    session,
    isFullyLoaded,
    companies,
    activeCompany,
    hasCompanyAccess,
    companyError,
    signUp,
    signIn,
    signOut,
    resetPassword,
    switchCompany
  ])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
